'use client'

import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'

interface InfoButtonProps {
  tooltipText: string
  className?: string
}

const InfoButton: React.FC<InfoButtonProps> = ({ tooltipText, className = '' }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<'left' | 'center' | 'right'>('center')
  const buttonRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isHovered && buttonRef.current && tooltipRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const margin = 16 // 1rem margin

      // Calculate if tooltip would overflow on left or right
      const tooltipLeft = buttonRect.left + buttonRect.width / 2 - tooltipRect.width / 2
      const tooltipRight = tooltipLeft + tooltipRect.width

      if (tooltipLeft < margin) {
        setTooltipPosition('left')
      } else if (tooltipRight > viewportWidth - margin) {
        setTooltipPosition('right')
      } else {
        setTooltipPosition('center')
      }
    }
  }, [isHovered])

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        ref={buttonRef}
        className={clsx(
          'inline-flex size-5 items-center justify-center rounded-full bg-black',
          'text-xs font-medium text-white transition-colors hover:bg-gray-800',
          'ml-[5px] border-2 border-black focus:outline-none',
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label="More information"
      >
        i
      </button>

      {isHovered && (
        <div
          className={clsx(
            'absolute bottom-full z-50 mb-2',
            tooltipPosition === 'center' && 'left-1/2 -translate-x-1/2',
            tooltipPosition === 'left' && 'left-0',
            tooltipPosition === 'right' && 'right-0',
          )}
        >
          <div
            ref={tooltipRef}
            className={clsx(
              'relative w-64 max-w-[calc(100vw-2rem)] rounded-lg px-3 py-2',
              'text-sm text-white shadow-lg sm:w-72',
            )}
            style={{ backgroundColor: 'rgb(33, 33, 33)' }}
          >
            <div className="text-center">{tooltipText}</div>
            <div
              className={clsx(
                'absolute top-full size-0 border-x-4 border-t-4 border-transparent',
                tooltipPosition === 'center' && 'left-1/2 -translate-x-1/2',
                tooltipPosition === 'left' && 'left-4',
                tooltipPosition === 'right' && 'right-4',
              )}
              style={{ borderTopColor: 'rgb(33, 33, 33)' }}
            ></div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InfoButton
